import { useState } from 'react'
import html2canvas from 'html2canvas'
import { useWedding } from '../../../context/WeddingContext'
import { useNavigate } from 'react-router-dom'
import { Check, Crown, Lock, Download, Upload, MessageCircle } from 'lucide-react'

// ── Free Templates (simple) ───────────────────────────────
const FREE_TEMPLATES = [
  { id: 'classic_ivory', name: 'Classic Ivory', tier: 'free',
    swatch: ['#FAF7F0','#C4826A','#1A1A1A'] },
  { id: 'garden_sage',   name: 'Garden Sage',   tier: 'free',
    swatch: ['#F2F5F2','#7A917A','#1A1A1A'] },
  { id: 'midnight',      name: 'Midnight',      tier: 'free',
    swatch: ['#1A1A2E','#C4826A','#FFFFFF'] },
]

// ── Premium Templates ────────────────────────────────────
// Image templates: a designed background (text-free) + an overlaid text zone
// defined as fractions of the image. Drop new art in /public/invite-templates/
// and add an entry here, tuning `zone` to the artwork's clear area.
const PREMIUM_TEMPLATES = [
  { id: 'img_peony', name: 'Peony Garden', tier: 'premium', type: 'image',
    src: '/invite-templates/inv1.png', aspect: 1054/1493,
    zone: { left: 0.20, right: 0.20, top: 0.19, bottom: 0.14 },
    ink: '#5C4033', accent: '#BD5247', sub: '#9A7B68',
    titleColor: '#BD5247', swatch: ['#F2E8E2','#BD5247','#7A8B5A'] },
  { id: 'img_rosewood', name: 'Rosewood Corners', tier: 'premium', type: 'image', layout: 'editorial',
    src: '/invite-templates/inv2.png', aspect: 845/1339,
    zone: { left: 0.17, right: 0.17, top: 0.20, bottom: 0.18 },
    ink: '#5A4650', accent: '#C2778C', sub: '#9E8590',
    titleColor: '#C2778C', swatch: ['#F3ECE4','#C2778C','#8AA77E'] },
  { id: 'img_gilded', name: 'Gilded Botanical', tier: 'premium', type: 'image', layout: 'goldbar',
    src: '/invite-templates/inv3.png', aspect: 1058/1486,
    zone: { left: 0.18, right: 0.18, top: 0.16, bottom: 0.13 },
    ink: '#3E3228', accent: '#B8923E', sub: '#A88B52',
    titleColor: '#B8923E', swatch: ['#F3EFE4','#B8923E','#A8AE8A'] },
  { id: 'img_wreath', name: 'Azure Wreath', tier: 'premium', type: 'image', layout: 'wreath',
    src: '/invite-templates/inv4.png', aspect: 1038/1516,
    headerZone: { left: 0.12, right: 0.12, top: 0.065, bottom: 0.875 },
    nameZone: { left: 0.22, right: 0.22, top: 0.37, bottom: 0.33 },
    bodyZone: { left: 0.08, right: 0.08, top: 0.80, bottom: 0.04 },
    ink: '#3A3633', accent: '#C2A24E', sub: '#7E7468',
    titleColor: '#C2A24E', swatch: ['#EFEAE2','#C2A24E','#6FA0C0'] },
  { id: 'img_blush', name: 'Pressed Blossom', tier: 'premium', type: 'image', layout: 'pressed',
    src: '/invite-templates/inv5.png', aspect: 442/632,
    zone: { left: 0.15, right: 0.15, top: 0.15, bottom: 0.12 },
    ink: '#46403A', accent: '#B06A86', sub: '#8C8278',
    titleColor: '#B06A86', swatch: ['#F1E9E4','#B06A86','#C2A24E'] },
  { id: 'img_burgundy', name: 'Burgundy Bloom', tier: 'premium', type: 'image', layout: 'burgundy',
    src: '/invite-templates/inv6.png', aspect: 450/634,
    zone: { left: 0.44, right: 0.05, top: 0.16, bottom: 0.06 },
    ink: '#1F1B1A', accent: '#8A2A3C', sub: '#6A5A55',
    titleColor: '#8A2A3C', swatch: ['#FBF6F5','#8A2A3C','#7E8B5F'] },
  { id: 'img_pine', name: 'Winter Greenery', tier: 'premium', type: 'image', layout: 'leaf',
    src: '/invite-templates/inv7.png', aspect: 450/634,
    zone: { left: 0.12, right: 0.12, top: 0.20, bottom: 0.14 },
    ink: '#46514A', accent: '#8FA086', sub: '#8A8F86',
    titleColor: '#46514A', swatch: ['#F3F6F1','#7C8C72','#46514A'] },
  { id: 'img_eucalyptus', name: 'Gilded Eucalyptus', tier: 'premium', type: 'image', layout: 'goldleaf',
    src: '/invite-templates/inv8.jpeg', aspect: 450/634,
    zone: { left: 0.11, right: 0.11, top: 0.14, bottom: 0.10 },
    ink: '#262626', accent: '#9A8B5E', sub: '#7C7A72',
    titleColor: '#9A8B5E', swatch: ['#F5F6F1','#9A8B5E','#8C9A7C'] },
]

// Capitalise the first letter of each word — names render as the couple typed
// them, so "elondra" becomes "Elondra" consistently on every card and font.
const titleCase = (s) => (s || '').replace(/(^|\s)\S/g, (c) => c.toUpperCase())

// ─────────────────────────────────────────────────────────
// FREE CARD RENDERER
// ─────────────────────────────────────────────────────────
function FreeCard({ id, wedding, event }) {
  const p1 = titleCase(wedding?.partner1_name) || 'Partner One'
  const p2 = titleCase(wedding?.partner2_name) || 'Partner Two'
  const evDate = event?.date ? new Date(event.date).toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long', year:'numeric' }) : 'Date To Be Confirmed'
  const evVenue = event?.venue || 'Venue To Be Confirmed'
  const city = wedding?.city || ''

  const themes = {
    classic_ivory: { bg:'#FAF7F0', acc:'#C4826A', text:'#2A1A0A', sub:'#6B5A4A', border:'#E8DDD0' },
    garden_sage:   { bg:'#F2F5F2', acc:'#7A917A', text:'#1A2A1A', sub:'#4A6050', border:'#C5D3C5' },
    midnight:      { bg:'#1A1A2E', acc:'#C4826A', text:'#FFFFFF', sub:'rgba(255,255,255,0.55)', border:'rgba(255,255,255,0.12)' },
  }
  const c = themes[id] || themes.classic_ivory

  return (
    <div style={{ width:340, background:c.bg, border:`2px solid ${c.border}`, borderRadius:16, padding:'2.5rem 2rem', textAlign:'center', boxShadow:'0 8px 32px rgba(0,0,0,0.1)', position:'relative' }}>
      <div style={{ position:'absolute', top:12, left:12, width:28, height:28, border:`1.5px solid ${c.border}`, borderRight:'none', borderBottom:'none' }} />
      <div style={{ position:'absolute', top:12, right:12, width:28, height:28, border:`1.5px solid ${c.border}`, borderLeft:'none', borderBottom:'none' }} />
      <div style={{ position:'absolute', bottom:12, left:12, width:28, height:28, border:`1.5px solid ${c.border}`, borderRight:'none', borderTop:'none' }} />
      <div style={{ position:'absolute', bottom:12, right:12, width:28, height:28, border:`1.5px solid ${c.border}`, borderLeft:'none', borderTop:'none' }} />
      <p style={{ fontSize:'0.58rem', fontWeight:700, letterSpacing:'0.18em', color:c.acc, textTransform:'uppercase', margin:'0 0 1rem' }}>You are cordially invited</p>
      <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', justifyContent:'center', marginBottom:'1rem' }}>
        <div style={{ height:1, width:40, background:c.border }} />
        <div style={{ width:6, height:6, borderRadius:'50%', background:c.acc }} />
        <div style={{ height:1, width:40, background:c.border }} />
      </div>
      <h2 style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:400, fontSize:'1.6rem', color:c.text, lineHeight:1.2, margin:'0 0 0.2rem' }}>{p1}</h2>
      <p style={{ fontFamily:'Cormorant Garamond, serif', fontStyle:'italic', fontSize:'0.9rem', color:c.acc, margin:'0 0 0.2rem' }}>&amp;</p>
      <h2 style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:400, fontSize:'1.6rem', color:c.text, lineHeight:1.2, margin:'0 0 1.25rem' }}>{p2}</h2>
      <div style={{ background:c.acc, borderRadius:100, display:'inline-block', padding:'0.3rem 1.25rem', marginBottom:'1.25rem' }}>
        <p style={{ fontSize:'0.62rem', fontWeight:700, letterSpacing:'0.1em', color:'#fff', textTransform:'uppercase', margin:0 }}>{event?.type||'Wedding Celebration'}</p>
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', justifyContent:'center', marginBottom:'1rem' }}>
        <div style={{ height:1, flex:1, background:c.border }} /><span style={{ color:c.acc }}>✦</span><div style={{ height:1, flex:1, background:c.border }} />
      </div>
      <p style={{ fontSize:'0.85rem', fontWeight:700, color:c.text, margin:'0 0 0.3rem', fontFamily:'Cormorant Garamond, serif' }}>{evDate}</p>
      <p style={{ fontSize:'0.78rem', color:c.sub, margin:'0 0 0.2rem' }}>{evVenue}</p>
      {city && <p style={{ fontSize:'0.72rem', color:c.sub, margin:0 }}>{city}</p>}
      <div style={{ marginTop:'1.5rem', paddingTop:'1rem', borderTop:`1px solid ${c.border}` }}>
        <p style={{ fontSize:'0.62rem', color:c.sub, margin:0, letterSpacing:'0.06em' }}>Kindly RSVP via your personal invite link</p>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// PREMIUM CARD 1 — Blush Watercolor Floral (Image 1)
// ─────────────────────────────────────────────────────────
function BlushFloralCard({ wedding, event }) {
  const p1 = titleCase(wedding?.partner1_name) || 'Partner One'
  const p2 = titleCase(wedding?.partner2_name) || 'Partner Two'
  const evDate = event?.date ? new Date(event.date).toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long', year:'numeric' }) : 'Date To Be Confirmed'
  const evVenue = event?.venue || 'Venue To Be Confirmed'
  const city = wedding?.city || ''

  return (
    <div style={{ width:340, background:'#FAF7F2', border:'1.5px solid #EDE0D4', borderRadius:16, padding:'2.25rem 2rem', textAlign:'center', boxShadow:'0 12px 48px rgba(196,130,106,0.15)', position:'relative', overflow:'hidden', minHeight:480 }}>

      {/* Watercolor mint wash — top */}
      <div style={{ position:'absolute', top:-30, left:-20, width:220, height:200, background:'radial-gradient(ellipse, rgba(180,210,190,0.35) 0%, transparent 70%)', pointerEvents:'none' }} />
      {/* Watercolor mint wash — bottom */}
      <div style={{ position:'absolute', bottom:-20, right:-20, width:200, height:180, background:'radial-gradient(ellipse, rgba(180,210,190,0.3) 0%, transparent 70%)', pointerEvents:'none' }} />

      {/* Top-right floral cluster */}
      <svg style={{ position:'absolute', top:-4, right:-4, width:140, height:150, pointerEvents:'none' }} viewBox="0 0 140 150">
        {/* Roses */}
        <circle cx="100" cy="28" r="22" fill="#E8A8B0" opacity="0.85"/>
        <circle cx="100" cy="28" r="14" fill="#F0B8BC" opacity="0.9"/>
        <circle cx="100" cy="28" r="7"  fill="#F8CCCC" opacity="0.95"/>
        <circle cx="72"  cy="50" r="13" fill="#EDBBAA" opacity="0.8"/>
        <circle cx="72"  cy="50" r="8"  fill="#F5CCBB" opacity="0.9"/>
        <circle cx="118" cy="52" r="10" fill="#E8A8B0" opacity="0.7"/>
        {/* Buds */}
        <ellipse cx="85" cy="70" rx="6" ry="9" fill="#E8B4AA" opacity="0.75"/>
        <ellipse cx="60" cy="38" rx="5" ry="8" fill="#F0C0B8" opacity="0.7"/>
        {/* Leaves */}
        <ellipse cx="60" cy="22" rx="12" ry="6" fill="#8AB88A" opacity="0.8" transform="rotate(-30 60 22)"/>
        <ellipse cx="120" cy="70" rx="14" ry="5" fill="#7AAA7A" opacity="0.75" transform="rotate(20 120 70)"/>
        <ellipse cx="45" cy="55" rx="10" ry="4" fill="#9AC89A" opacity="0.7" transform="rotate(-20 45 55)"/>
        <ellipse cx="95" cy="80" rx="12" ry="4" fill="#8AB88A" opacity="0.65" transform="rotate(40 95 80)"/>
        {/* Gold stems */}
        <line x1="78" y1="65" x2="65" y2="90" stroke="#C9A055" strokeWidth="1" opacity="0.6"/>
        <line x1="95" y1="75" x2="110" y2="95" stroke="#C9A055" strokeWidth="1" opacity="0.5"/>
        <circle cx="68" cy="92" r="2" fill="#C9A055" opacity="0.5"/>
        <circle cx="112" cy="97" r="2" fill="#C9A055" opacity="0.5"/>
        <circle cx="55" cy="78" r="1.5" fill="#C9A055" opacity="0.4"/>
      </svg>

      {/* Bottom-left floral cluster */}
      <svg style={{ position:'absolute', bottom:-4, left:-4, width:150, height:160, pointerEvents:'none' }} viewBox="0 0 150 160">
        <circle cx="40"  cy="130" r="24" fill="#E8A8B0" opacity="0.85"/>
        <circle cx="40"  cy="130" r="15" fill="#F0B8BC" opacity="0.9"/>
        <circle cx="40"  cy="130" r="7"  fill="#F8CCCC" opacity="0.95"/>
        <circle cx="70"  cy="115" r="14" fill="#EDBBAA" opacity="0.75"/>
        <circle cx="70"  cy="115" r="8"  fill="#F5CCBB" opacity="0.85"/>
        <circle cx="20"  cy="108" r="10" fill="#E8A8B0" opacity="0.7"/>
        <ellipse cx="55" cy="90"  rx="6" ry="9" fill="#E8B4AA" opacity="0.75"/>
        <ellipse cx="90" cy="130" rx="7" ry="10" fill="#F0C0B8" opacity="0.7"/>
        <ellipse cx="85" cy="140" rx="12" ry="5" fill="#8AB88A" opacity="0.8" transform="rotate(-20 85 140)"/>
        <ellipse cx="30" cy="90"  rx="14" ry="5" fill="#7AAA7A" opacity="0.75" transform="rotate(30 30 90)"/>
        <ellipse cx="110" cy="120" rx="10" ry="4" fill="#9AC89A" opacity="0.7" transform="rotate(-10 110 120)"/>
        <line x1="60" y1="100" x2="50" y2="80" stroke="#C9A055" strokeWidth="1" opacity="0.6"/>
        <circle cx="50" cy="78" r="2" fill="#C9A055" opacity="0.5"/>
        <circle cx="95" cy="105" r="1.5" fill="#C9A055" opacity="0.45"/>
      </svg>

      {/* Gold speckles */}
      {[[145,60],[130,100],[25,80],[40,170],[155,160],[20,40]].map(([x,y],i) => (
        <div key={i} style={{ position:'absolute', left:x, top:y, width:4, height:4, borderRadius:'50%', background:'#C9A055', opacity:0.35 }} />
      ))}

      {/* Content */}
      <div style={{ position:'relative', zIndex:2 }}>
        <p style={{ fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.18em', color:'#8B6A50', textTransform:'uppercase', margin:'0.5rem 0 0.25rem' }}>Together with their families</p>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', justifyContent:'center', margin:'0.5rem 0' }}>
          <div style={{ height:1, width:30, background:'#E0C8B8' }} />
          <div style={{ width:5, height:5, borderRadius:'50%', background:'#C4826A' }} />
          <div style={{ height:1, width:30, background:'#E0C8B8' }} />
        </div>
        <h2 style={{ fontFamily:'Great Vibes, cursive', fontWeight:600, fontSize:'2.1rem', color:'#3A2018', lineHeight:1.15, margin:'0.5rem 0 0' }}>{p1}</h2>
        <p style={{ fontFamily:'Cormorant Garamond, serif', fontStyle:'italic', fontSize:'0.85rem', color:'#8B6A50', margin:'0.1rem 0' }}>AND</p>
        <h2 style={{ fontFamily:'Great Vibes, cursive', fontWeight:600, fontSize:'2.1rem', color:'#3A2018', lineHeight:1.15, margin:'0 0 1rem' }}>{p2}</h2>
        <p style={{ fontSize:'0.6rem', fontWeight:600, letterSpacing:'0.12em', color:'#8B6A50', textTransform:'uppercase', margin:'0 0 0.75rem', lineHeight:1.7 }}>
          Request the pleasure of your company<br/>at the ceremony of their wedding
        </p>
        {/* Date display */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'0.75rem', margin:'0.75rem 0', padding:'0.75rem', background:'rgba(255,255,255,0.6)', borderRadius:10 }}>
          {event?.date ? (
            <>
              <div style={{ textAlign:'center' }}>
                <p style={{ fontSize:'0.62rem', fontWeight:700, letterSpacing:'0.1em', color:'#8B6A50', margin:0 }}>{new Date(event.date).toLocaleDateString('en-GB',{weekday:'short'}).toUpperCase()}</p>
              </div>
              <div style={{ width:1, height:30, background:'#E0C8B8' }} />
              <div style={{ textAlign:'center' }}>
                <p style={{ fontFamily:'Cormorant Garamond, serif', fontSize:'1.8rem', color:'#3A2018', margin:0, lineHeight:1 }}>{new Date(event.date).getDate()}</p>
              </div>
              <div style={{ width:1, height:30, background:'#E0C8B8' }} />
              <div style={{ textAlign:'center' }}>
                <p style={{ fontSize:'0.62rem', fontWeight:700, letterSpacing:'0.08em', color:'#8B6A50', margin:0 }}>{new Date(event.date).toLocaleDateString('en-GB',{month:'short'}).toUpperCase()}</p>
                <p style={{ fontSize:'0.62rem', color:'#8B6A50', margin:0 }}>{new Date(event.date).getFullYear()}</p>
              </div>
            </>
          ) : <p style={{ fontFamily:'Cormorant Garamond, serif', fontSize:'0.85rem', color:'#3A2018', margin:0 }}>Date To Be Confirmed</p>}
        </div>
        <p style={{ fontSize:'0.78rem', fontWeight:700, color:'#3A2018', margin:'0.5rem 0 0.2rem' }}>{evVenue}</p>
        {city && <p style={{ fontSize:'0.72rem', color:'#8B6A50', margin:0 }}>{city}</p>}
        <p style={{ fontFamily:'Great Vibes, cursive', fontSize:'1rem', color:'#C4826A', margin:'0.75rem 0 0' }}>reception to follow</p>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// PREMIUM CARD 2 — Gold Circle Wreath (Image 2)
// ─────────────────────────────────────────────────────────
function GoldWreathCard({ wedding, event }) {
  const p1 = titleCase(wedding?.partner1_name) || 'Partner One'
  const p2 = titleCase(wedding?.partner2_name) || 'Partner Two'
  const evDate = event?.date ? new Date(event.date).toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long', year:'numeric' }) : 'Date To Be Confirmed'
  const evVenue = event?.venue || 'Venue To Be Confirmed'
  const city = wedding?.city || ''

  return (
    <div style={{ width:340, background:'#F5F0E8', border:'1.5px solid #E0D0B0', borderRadius:16, padding:'2rem', textAlign:'center', boxShadow:'0 12px 48px rgba(201,168,76,0.18)', position:'relative', overflow:'hidden' }}>

      {/* Top-right gold botanical corner (line art) */}
      <svg style={{ position:'absolute', top:8, right:8, width:80, height:80, opacity:0.6 }} viewBox="0 0 80 80">
        <path d="M70,10 Q50,30 30,25 Q20,20 15,35" stroke="#C9A84C" strokeWidth="1.2" fill="none"/>
        <ellipse cx="60" cy="15" rx="10" ry="5" fill="none" stroke="#C9A84C" strokeWidth="1" transform="rotate(-30 60 15)"/>
        <ellipse cx="45" cy="22" rx="8" ry="4" fill="none" stroke="#C9A84C" strokeWidth="1" transform="rotate(-10 45 22)"/>
        <ellipse cx="30" cy="28" rx="7" ry="3.5" fill="none" stroke="#C9A84C" strokeWidth="1" transform="rotate(15 30 28)"/>
      </svg>
      {/* Bottom-left gold botanical corner */}
      <svg style={{ position:'absolute', bottom:8, left:8, width:80, height:80, opacity:0.6, transform:'rotate(180deg)' }} viewBox="0 0 80 80">
        <path d="M70,10 Q50,30 30,25 Q20,20 15,35" stroke="#C9A84C" strokeWidth="1.2" fill="none"/>
        <ellipse cx="60" cy="15" rx="10" ry="5" fill="none" stroke="#C9A84C" strokeWidth="1" transform="rotate(-30 60 15)"/>
        <ellipse cx="45" cy="22" rx="8" ry="4" fill="none" stroke="#C9A84C" strokeWidth="1" transform="rotate(-10 45 22)"/>
        <ellipse cx="30" cy="28" rx="7" ry="3.5" fill="none" stroke="#C9A84C" strokeWidth="1" transform="rotate(15 30 28)"/>
      </svg>

      <p style={{ fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.16em', color:'#8A7040', textTransform:'uppercase', margin:'0 0 1rem' }}>Save the Date</p>

      {/* Gold circle wreath */}
      <div style={{ position:'relative', width:200, height:200, margin:'0 auto 1.25rem' }}>
        {/* Gold ring */}
        <div style={{ position:'absolute', inset:0, borderRadius:'50%', border:'2px solid #C9A84C', boxShadow:'0 0 0 4px rgba(201,168,76,0.1)' }} />

        {/* Blue flowers around ring — left side */}
        <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%' }} viewBox="0 0 200 200">
          {/* Blue watercolor leaves */}
          <ellipse cx="18" cy="90" rx="18" ry="8" fill="#6A9BC4" opacity="0.7" transform="rotate(-20 18 90)"/>
          <ellipse cx="25" cy="115" rx="16" ry="7" fill="#7AAAD4" opacity="0.65" transform="rotate(10 25 115)"/>
          <ellipse cx="30" cy="70" rx="14" ry="6" fill="#5A8AB4" opacity="0.6" transform="rotate(-40 30 70)"/>
          <ellipse cx="22" cy="140" rx="14" ry="6" fill="#6A9BC4" opacity="0.6" transform="rotate(30 22 140)"/>
          <ellipse cx="40" cy="155" rx="12" ry="5" fill="#7AAAD4" opacity="0.55" transform="rotate(50 40 155)"/>
          {/* Gold roses left */}
          <circle cx="15" cy="78" r="10" fill="#D4A843" opacity="0.8"/>
          <circle cx="15" cy="78" r="6"  fill="#E8C060" opacity="0.85"/>
          <circle cx="15" cy="78" r="3"  fill="#F0D080" opacity="0.9"/>
          <circle cx="20" cy="130" r="9" fill="#C9A055" opacity="0.75"/>
          <circle cx="20" cy="130" r="5" fill="#DDB865" opacity="0.8"/>
          {/* Blue flowers bottom */}
          <circle cx="65" cy="180" r="8"  fill="#6A9BC4" opacity="0.7"/>
          <circle cx="80" cy="188" r="6"  fill="#7AAAD4" opacity="0.65"/>
          <circle cx="105" cy="192" r="7" fill="#5A8AB4" opacity="0.6"/>
          <circle cx="125" cy="185" r="8" fill="#6A9BC4" opacity="0.65"/>
          {/* Small blue petals */}
          <circle cx="65" cy="180" r="4" fill="#5090C0" opacity="0.8"/>
          <circle cx="105" cy="192" r="3.5" fill="#5090C0" opacity="0.75"/>
          <circle cx="125" cy="185" r="4" fill="#5090C0" opacity="0.7"/>
        </svg>

        {/* Names inside circle */}
        <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
          <h2 style={{ fontFamily:'Great Vibes, cursive', fontWeight:600, fontSize:'2rem', color:'#2A1A0A', lineHeight:1.1, margin:0 }}>{p1}</h2>
          <p style={{ fontFamily:'Great Vibes, cursive', fontStyle:'italic', fontSize:'1rem', color:'#C9A84C', margin:'0.1rem 0' }}>&amp;</p>
          <h2 style={{ fontFamily:'Great Vibes, cursive', fontWeight:600, fontSize:'2rem', color:'#2A1A0A', lineHeight:1.1, margin:0 }}>{p2}</h2>
        </div>
      </div>

      {/* Date row */}
      {event?.date ? (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'0.75rem', marginBottom:'0.75rem' }}>
          <div style={{ textAlign:'right' }}>
            <p style={{ fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.1em', color:'#8A7040', margin:0 }}>{new Date(event.date).toLocaleDateString('en-GB',{weekday:'long'}).toUpperCase()}</p>
            <p style={{ fontSize:'0.6rem', color:'#8A7040', margin:0 }}>{new Date(event.date).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}</p>
          </div>
          <div style={{ width:1, height:32, background:'#C9A84C' }} />
          <p style={{ fontFamily:'Cormorant Garamond, serif', fontSize:'2rem', color:'#2A1A0A', margin:0, lineHeight:1 }}>{new Date(event.date).getDate()}</p>
          <div style={{ width:1, height:32, background:'#C9A84C' }} />
          <div style={{ textAlign:'left' }}>
            <p style={{ fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.08em', color:'#8A7040', margin:0 }}>{new Date(event.date).toLocaleDateString('en-GB',{month:'long'}).toUpperCase()}</p>
            <p style={{ fontSize:'0.6rem', color:'#8A7040', margin:0 }}>{new Date(event.date).getFullYear()}</p>
          </div>
        </div>
      ) : <p style={{ fontFamily:'Cormorant Garamond, serif', fontSize:'0.85rem', color:'#2A1A0A', margin:'0 0 0.75rem' }}>Date To Be Confirmed</p>}

      <p style={{ fontSize:'0.78rem', color:'#5A4A30', margin:'0 0 0.15rem', fontWeight:600 }}>{evVenue}</p>
      {city && <p style={{ fontSize:'0.72rem', color:'#8A7040', margin:0 }}>{city}</p>}
      <p style={{ fontSize:'0.55rem', fontWeight:700, letterSpacing:'0.12em', color:'#C9A84C', margin:'1rem 0 0', textTransform:'uppercase' }}>Okoiya</p>
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// PREMIUM CARD 3 — Navy & Gold Line Art (Image 3)
// ─────────────────────────────────────────────────────────
function NavyGoldCard({ wedding, event }) {
  const p1 = titleCase(wedding?.partner1_name) || 'Partner One'
  const p2 = titleCase(wedding?.partner2_name) || 'Partner Two'
  const evVenue = event?.venue || 'Venue To Be Confirmed'
  const city = wedding?.city || ''

  return (
    <div style={{ width:340, background:'#1C2340', borderRadius:16, padding:0, overflow:'hidden', boxShadow:'0 16px 56px rgba(0,0,0,0.45)', position:'relative' }}>

      {/* Diagonal gold frame lines */}
      <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', pointerEvents:'none' }} viewBox="0 0 340 480" preserveAspectRatio="none">
        <line x1="30" y1="30" x2="200" y2="30"   stroke="#D4AF37" strokeWidth="0.8" opacity="0.5"/>
        <line x1="30" y1="30" x2="30"  y2="160"  stroke="#D4AF37" strokeWidth="0.8" opacity="0.5"/>
        <line x1="140" y1="320" x2="310" y2="320" stroke="#D4AF37" strokeWidth="0.8" opacity="0.5"/>
        <line x1="310" y1="320" x2="310" y2="450" stroke="#D4AF37" strokeWidth="0.8" opacity="0.5"/>
      </svg>

      {/* Top-left gold floral cluster */}
      <svg style={{ position:'absolute', top:0, left:0, width:180, height:200, pointerEvents:'none' }} viewBox="0 0 180 200">
        {/* Flowers gold outline */}
        <circle cx="70" cy="55" r="28" fill="none" stroke="#D4AF37" strokeWidth="1.5" opacity="0.7"/>
        <circle cx="70" cy="55" r="18" fill="rgba(212,175,55,0.1)" stroke="#D4AF37" strokeWidth="1" opacity="0.6"/>
        <circle cx="70" cy="55" r="10" fill="rgba(212,175,55,0.15)" stroke="#D4AF37" strokeWidth="0.8" opacity="0.7"/>
        <circle cx="30" cy="90" r="20" fill="none" stroke="#D4AF37" strokeWidth="1.2" opacity="0.6"/>
        <circle cx="30" cy="90" r="12" fill="rgba(212,175,55,0.08)" stroke="#D4AF37" strokeWidth="0.8" opacity="0.55"/>
        <circle cx="110" cy="40" r="16" fill="none" stroke="#D4AF37" strokeWidth="1" opacity="0.55"/>
        {/* Petals on large flower */}
        {[0,45,90,135,180,225,270,315].map((a,i)=>(
          <ellipse key={i} cx={70+32*Math.cos(a*Math.PI/180)} cy={55+32*Math.sin(a*Math.PI/180)} rx="9" ry="5" fill="none" stroke="#D4AF37" strokeWidth="1" opacity="0.5" transform={`rotate(${a} ${70+32*Math.cos(a*Math.PI/180)} ${55+32*Math.sin(a*Math.PI/180)})`}/>
        ))}
        {/* Leaves */}
        <path d="M90,100 Q110,80 140,90 Q120,110 90,100" fill="none" stroke="#D4AF37" strokeWidth="1" opacity="0.5"/>
        <path d="M20,120 Q0,100 10,75 Q25,95 20,120" fill="none" stroke="#D4AF37" strokeWidth="1" opacity="0.5"/>
        {/* Stems */}
        <line x1="70" y1="83" x2="60" y2="120" stroke="#D4AF37" strokeWidth="1" opacity="0.4"/>
        <line x1="30" y1="110" x2="20" y2="145" stroke="#D4AF37" strokeWidth="1" opacity="0.4"/>
        {/* Berries */}
        <circle cx="58" cy="125" r="3" fill="#D4AF37" opacity="0.5"/>
        <circle cx="65" cy="132" r="2.5" fill="#D4AF37" opacity="0.45"/>
        <circle cx="18" cy="148" r="3" fill="#D4AF37" opacity="0.45"/>
      </svg>

      {/* Bottom-right gold floral cluster */}
      <svg style={{ position:'absolute', bottom:0, right:0, width:170, height:190, pointerEvents:'none' }} viewBox="0 0 170 190">
        <circle cx="100" cy="135" r="26" fill="none" stroke="#D4AF37" strokeWidth="1.5" opacity="0.7"/>
        <circle cx="100" cy="135" r="16" fill="rgba(212,175,55,0.1)" stroke="#D4AF37" strokeWidth="1" opacity="0.6"/>
        <circle cx="100" cy="135" r="9"  fill="rgba(212,175,55,0.15)" stroke="#D4AF37" strokeWidth="0.8" opacity="0.7"/>
        <circle cx="145" cy="100" r="18" fill="none" stroke="#D4AF37" strokeWidth="1.2" opacity="0.6"/>
        <circle cx="145" cy="100" r="11" fill="rgba(212,175,55,0.08)" stroke="#D4AF37" strokeWidth="0.8" opacity="0.55"/>
        {[0,45,90,135,180,225,270,315].map((a,i)=>(
          <ellipse key={i} cx={100+30*Math.cos(a*Math.PI/180)} cy={135+30*Math.sin(a*Math.PI/180)} rx="8" ry="5" fill="none" stroke="#D4AF37" strokeWidth="1" opacity="0.5" transform={`rotate(${a} ${100+30*Math.cos(a*Math.PI/180)} ${135+30*Math.sin(a*Math.PI/180)})`}/>
        ))}
        <path d="M80,80 Q60,65 40,75 Q55,88 80,80" fill="none" stroke="#D4AF37" strokeWidth="1" opacity="0.5"/>
        <line x1="100" y1="109" x2="88" y2="75" stroke="#D4AF37" strokeWidth="1" opacity="0.4"/>
        <circle cx="86" cy="72" r="2.5" fill="#D4AF37" opacity="0.5"/>
        <circle cx="92" cy="65" r="2" fill="#D4AF37" opacity="0.45"/>
      </svg>

      {/* Gold dot speckles */}
      {[[280,80],[300,200],[40,300],[60,420],[250,350]].map(([x,y],i) => (
        <div key={i} style={{ position:'absolute', left:x, top:y, width:3, height:3, borderRadius:'50%', background:'#D4AF37', opacity:0.4 }} />
      ))}

      {/* Content */}
      <div style={{ position:'relative', zIndex:2, padding:'2.25rem 2rem', textAlign:'center' }}>
        <p style={{ fontSize:'0.58rem', fontWeight:700, letterSpacing:'0.18em', color:'rgba(212,175,55,0.8)', textTransform:'uppercase', margin:'0 0 1.5rem', lineHeight:1.8 }}>
          Please join us for<br/>the wedding of
        </p>
        <h2 style={{ fontFamily:'Great Vibes, cursive', fontWeight:600, fontSize:'2.5rem', color:'#FFFFFF', lineHeight:1.1, margin:'0 0 0.15rem' }}>{p1}</h2>
        <p style={{ fontFamily:'Cormorant Garamond, serif', fontStyle:'italic', fontSize:'0.85rem', color:'rgba(212,175,55,0.85)', margin:'0.25rem 0' }}>and</p>
        <h2 style={{ fontFamily:'Great Vibes, cursive', fontWeight:600, fontSize:'2.5rem', color:'#FFFFFF', lineHeight:1.1, margin:'0 0 2rem' }}>{p2}</h2>

        <p style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.7)', margin:'0 0 0.3rem' }}>{evVenue}{city ? `, ${city}` : ''}</p>

        {/* Date block */}
        {event?.date ? (
          <div style={{ display:'flex', alignItems:'stretch', justifyContent:'center', gap:0, margin:'1.25rem auto', maxWidth:220, border:'1px solid rgba(212,175,55,0.35)' }}>
            <div style={{ flex:1, padding:'0.6rem 0.5rem', borderRight:'1px solid rgba(212,175,55,0.35)', textAlign:'center' }}>
              <p style={{ fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.1em', color:'rgba(212,175,55,0.8)', margin:0 }}>{new Date(event.date).toLocaleDateString('en-GB',{weekday:'short'}).toUpperCase()}</p>
              <p style={{ fontSize:'0.55rem', color:'rgba(255,255,255,0.5)', margin:0 }}>4.00 P.M.</p>
            </div>
            <div style={{ padding:'0.5rem 1rem', textAlign:'center', background:'rgba(212,175,55,0.1)' }}>
              <p style={{ fontFamily:'Cormorant Garamond, serif', fontSize:'1.75rem', color:'#FFFFFF', margin:0, lineHeight:1 }}>{new Date(event.date).getDate()}</p>
            </div>
            <div style={{ flex:1, padding:'0.6rem 0.5rem', borderLeft:'1px solid rgba(212,175,55,0.35)', textAlign:'center' }}>
              <p style={{ fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.08em', color:'rgba(212,175,55,0.8)', margin:0 }}>{new Date(event.date).toLocaleDateString('en-GB',{month:'short'}).toUpperCase()}</p>
              <p style={{ fontSize:'0.55rem', color:'rgba(255,255,255,0.5)', margin:0 }}>{new Date(event.date).getFullYear()}</p>
            </div>
          </div>
        ) : <p style={{ fontFamily:'Cormorant Garamond, serif', fontSize:'0.85rem', color:'rgba(255,255,255,0.7)', margin:'1rem 0' }}>Date To Be Confirmed</p>}

        <p style={{ fontSize:'0.52rem', fontWeight:700, letterSpacing:'0.14em', color:'rgba(212,175,55,0.6)', margin:'1.5rem 0 0', textTransform:'uppercase' }}>Okoiya</p>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// PREMIUM CARD 4 — Bold Monogram (Image 4)
// ─────────────────────────────────────────────────────────
function BoldMonoCard({ wedding, event }) {
  const p1 = titleCase(wedding?.partner1_name) || 'Partner One'
  const p2 = titleCase(wedding?.partner2_name) || 'Partner Two'
  const evDate = event?.date ? new Date(event.date).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' }) : 'Date To Be Confirmed'
  const evVenue = event?.venue || 'Venue To Be Confirmed'
  const city = wedding?.city || ''
  const i1 = p1[0]?.toUpperCase() || 'A'
  const i2 = p2[0]?.toUpperCase() || 'B'

  return (
    <div style={{ width:340, background:'#F5F0E8', border:'1.5px solid #E0D8CC', borderRadius:16, overflow:'hidden', boxShadow:'0 12px 40px rgba(0,0,0,0.12)', position:'relative', minHeight:460 }}>

      {/* Giant background initials */}
      <div style={{ position:'absolute', top:-20, left:-10, fontFamily:'Cormorant Garamond, serif', fontSize:'14rem', fontWeight:700, color:'rgba(26,26,26,0.07)', lineHeight:1, userSelect:'none', pointerEvents:'none' }}>{i1}</div>
      <div style={{ position:'absolute', bottom:-30, right:-10, fontFamily:'Cormorant Garamond, serif', fontSize:'14rem', fontWeight:700, color:'rgba(26,26,26,0.07)', lineHeight:1, userSelect:'none', pointerEvents:'none' }}>{i2}</div>
      <div style={{ position:'absolute', top:'35%', left:-15, fontFamily:'Cormorant Garamond, serif', fontSize:'9rem', fontWeight:700, color:'rgba(26,26,26,0.06)', lineHeight:1, userSelect:'none', pointerEvents:'none' }}>&amp;</div>

      {/* Content */}
      <div style={{ position:'relative', zIndex:2, padding:'2.5rem 2rem' }}>
        <p style={{ fontFamily:'Cormorant Garamond, serif', fontStyle:'italic', fontSize:'0.78rem', color:'#6B6B6B', margin:'0 0 1.5rem' }}>Please join us for the wedding of</p>

        <div style={{ marginBottom:'1.5rem' }}>
          <p style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:700, fontSize:'1.6rem', letterSpacing:'0.08em', color:'#1A1A1A', margin:'0 0 0.1rem', textTransform:'uppercase' }}>{p1}</p>
          <p style={{ fontFamily:'Cormorant Garamond, serif', fontStyle:'italic', fontSize:'0.85rem', color:'#6B6B6B', margin:'0 0 0.1rem' }}>and</p>
          <p style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:700, fontSize:'1.6rem', letterSpacing:'0.08em', color:'#1A1A1A', margin:0, textTransform:'uppercase' }}>{p2}</p>
        </div>

        <div style={{ width:40, height:2, background:'#1A1A1A', marginBottom:'1.5rem' }} />

        <p style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:700, fontSize:'0.95rem', letterSpacing:'0.06em', color:'#1A1A1A', margin:'0 0 0.4rem', textTransform:'uppercase' }}>{evDate}</p>
        <p style={{ fontFamily:'Cormorant Garamond, serif', fontStyle:'italic', fontSize:'0.8rem', color:'#6B6B6B', margin:'0 0 0.25rem' }}>2 o'clock in the afternoon</p>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#6B6B6B', margin:'0 0 0.2rem' }}>{evVenue}</p>
        {city && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#6B6B6B', margin:0 }}>{city}</p>}

        <p style={{ fontFamily:'Cormorant Garamond, serif', fontStyle:'italic', fontSize:'0.78rem', color:'#6B6B6B', margin:'1.25rem 0 0' }}>Reception to follow</p>

        <p style={{ fontSize:'0.52rem', fontWeight:700, letterSpacing:'0.14em', color:'#BCBCBC', margin:'1.5rem 0 0', textTransform:'uppercase' }}>Okoiya</p>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// PREMIUM CARD 5 — Ivory Gold Embossed (Image 5)
// ─────────────────────────────────────────────────────────
function IvoryEmbossCard({ wedding, event }) {
  const p1 = titleCase(wedding?.partner1_name) || 'Partner One'
  const p2 = titleCase(wedding?.partner2_name) || 'Partner Two'
  const evVenue = event?.venue || 'Venue To Be Confirmed'
  const city = wedding?.city || ''

  return (
    <div style={{ width:340, background:'#FDFAF5', borderRadius:16, padding:'0.5rem', boxShadow:'0 16px 56px rgba(201,168,76,0.2)', position:'relative', overflow:'hidden' }}>

      {/* Outer gold border */}
      <div style={{ border:'2px solid #D4AF37', borderRadius:12, padding:'1.75rem 1.5rem', position:'relative', minHeight:460 }}>

        {/* Inner thin border */}
        <div style={{ position:'absolute', inset:8, border:'0.5px solid rgba(212,175,55,0.4)', borderRadius:8, pointerEvents:'none' }} />

        {/* Corner flourish — top-left */}
        <svg style={{ position:'absolute', top:6, left:6, width:40, height:40 }} viewBox="0 0 40 40">
          <path d="M2,2 L18,2 L18,4 L4,4 L4,18 L2,18 Z" fill="#D4AF37" opacity="0.6"/>
          <circle cx="20" cy="20" r="2" fill="#D4AF37" opacity="0.5"/>
        </svg>
        {/* Corner flourish — top-right */}
        <svg style={{ position:'absolute', top:6, right:6, width:40, height:40 }} viewBox="0 0 40 40">
          <path d="M38,2 L22,2 L22,4 L36,4 L36,18 L38,18 Z" fill="#D4AF37" opacity="0.6"/>
        </svg>
        {/* Corner flourish — bottom-left */}
        <svg style={{ position:'absolute', bottom:6, left:6, width:40, height:40 }} viewBox="0 0 40 40">
          <path d="M2,38 L18,38 L18,36 L4,36 L4,22 L2,22 Z" fill="#D4AF37" opacity="0.6"/>
        </svg>
        {/* Corner flourish — bottom-right */}
        <svg style={{ position:'absolute', bottom:6, right:6, width:40, height:40 }} viewBox="0 0 40 40">
          <path d="M38,38 L22,38 L22,36 L36,36 L36,22 L38,22 Z" fill="#D4AF37" opacity="0.6"/>
        </svg>

        {/* Embossed floral — top-right */}
        <svg style={{ position:'absolute', top:12, right:12, width:100, height:100, opacity:0.5 }} viewBox="0 0 100 100">
          <circle cx="70" cy="30" r="18" fill="none" stroke="#D4AF37" strokeWidth="1.5"/>
          <circle cx="70" cy="30" r="10" fill="rgba(212,175,55,0.2)" stroke="#D4AF37" strokeWidth="1"/>
          <circle cx="45" cy="55" r="14" fill="none" stroke="#D4AF37" strokeWidth="1.2"/>
          <circle cx="45" cy="55" r="8" fill="rgba(212,175,55,0.15)" stroke="#D4AF37" strokeWidth="0.8"/>
          <path d="M55,20 Q75,5 90,15 Q80,30 55,20" fill="rgba(212,175,55,0.15)" stroke="#D4AF37" strokeWidth="0.8"/>
          <path d="M80,50 Q95,40 95,60 Q85,65 80,50" fill="rgba(212,175,55,0.1)" stroke="#D4AF37" strokeWidth="0.8"/>
          <line x1="68" y1="48" x2="55" y2="68" stroke="#D4AF37" strokeWidth="0.8"/>
          <circle cx="53" cy="70" r="2" fill="#D4AF37"/>
        </svg>

        {/* Embossed floral — bottom-left */}
        <svg style={{ position:'absolute', bottom:12, left:12, width:100, height:100, opacity:0.5, transform:'rotate(180deg)' }} viewBox="0 0 100 100">
          <circle cx="70" cy="30" r="18" fill="none" stroke="#D4AF37" strokeWidth="1.5"/>
          <circle cx="70" cy="30" r="10" fill="rgba(212,175,55,0.2)" stroke="#D4AF37" strokeWidth="1"/>
          <circle cx="45" cy="55" r="14" fill="none" stroke="#D4AF37" strokeWidth="1.2"/>
          <circle cx="45" cy="55" r="8" fill="rgba(212,175,55,0.15)" stroke="#D4AF37" strokeWidth="0.8"/>
          <path d="M55,20 Q75,5 90,15 Q80,30 55,20" fill="rgba(212,175,55,0.15)" stroke="#D4AF37" strokeWidth="0.8"/>
          <line x1="68" y1="48" x2="55" y2="68" stroke="#D4AF37" strokeWidth="0.8"/>
          <circle cx="53" cy="70" r="2" fill="#D4AF37"/>
        </svg>

        {/* Content */}
        <div style={{ textAlign:'center', position:'relative', zIndex:2 }}>
          <div style={{ fontSize:'1.1rem', color:'#C9A84C', margin:'0 0 0.75rem', letterSpacing:'0.1em' }}>✦ ✦ ✦</div>
          <p style={{ fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.18em', color:'#8A7040', textTransform:'uppercase', margin:'0 0 0.75rem' }}>Together with their families</p>
          <h2 style={{ fontFamily:'Great Vibes, cursive', fontWeight:600, fontSize:'2.4rem', color:'#C9A84C', lineHeight:1.1, margin:'0 0 0.15rem' }}>{p1}</h2>
          <p style={{ fontFamily:'Great Vibes, cursive', fontStyle:'italic', fontSize:'1.2rem', color:'#8A7040', margin:'0 0 0.15rem' }}>&amp;</p>
          <h2 style={{ fontFamily:'Great Vibes, cursive', fontWeight:600, fontSize:'2.4rem', color:'#C9A84C', lineHeight:1.1, margin:'0 0 0.75rem' }}>{p2}</h2>
          <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', justifyContent:'center', margin:'0 0 0.75rem' }}>
            <div style={{ height:1, flex:1, background:'linear-gradient(to right, transparent, #D4AF37)' }} />
            <span style={{ color:'#D4AF37', fontSize:'0.8rem' }}>♥</span>
            <div style={{ height:1, flex:1, background:'linear-gradient(to left, transparent, #D4AF37)' }} />
          </div>
          <p style={{ fontSize:'0.6rem', fontWeight:600, letterSpacing:'0.14em', color:'#8A7040', textTransform:'uppercase', margin:'0 0 1rem', lineHeight:1.8 }}>
            Request the pleasure of your company<br/>at their wedding celebration
          </p>
          {event?.date ? (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'0.625rem', margin:'0 0 0.75rem', padding:'0.75rem', background:'rgba(212,175,55,0.05)', border:'0.5px solid rgba(212,175,55,0.3)', borderRadius:8 }}>
              <div>
                <p style={{ fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.1em', color:'#8A7040', margin:0 }}>{new Date(event.date).toLocaleDateString('en-GB',{weekday:'long'}).toUpperCase()}</p>
              </div>
              <div style={{ width:1, height:28, background:'#D4AF37', opacity:0.5 }} />
              <p style={{ fontFamily:'Cormorant Garamond, serif', fontSize:'1.6rem', color:'#1A1A1A', margin:0, lineHeight:1 }}>{new Date(event.date).getDate()}</p>
              <div style={{ width:1, height:28, background:'#D4AF37', opacity:0.5 }} />
              <div>
                <p style={{ fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.08em', color:'#8A7040', margin:0 }}>{new Date(event.date).toLocaleDateString('en-GB',{month:'long'}).toUpperCase()}</p>
                <p style={{ fontSize:'0.6rem', color:'#8A7040', margin:0 }}>{new Date(event.date).getFullYear()}</p>
              </div>
            </div>
          ) : <p style={{ fontFamily:'Cormorant Garamond, serif', fontSize:'0.85rem', color:'#1A1A1A', margin:'0 0 0.75rem' }}>Date To Be Confirmed</p>}
          <p style={{ fontSize:'0.72rem', fontWeight:700, color:'#1A1A1A', margin:'0 0 0.2rem' }}>{evVenue}</p>
          {city && <p style={{ fontSize:'0.68rem', color:'#8A7040', margin:'0 0 0.75rem' }}>{city}</p>}
          <div style={{ fontSize:'1rem', color:'#C9A84C', margin:'0.5rem 0', letterSpacing:'0.2em' }}>✦</div>
          <p style={{ fontSize:'0.55rem', fontWeight:700, letterSpacing:'0.14em', color:'#C9A84C', margin:0, textTransform:'uppercase' }}>Okoiya</p>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// IMAGE TEMPLATE RENDERER — designed background + overlaid text
// ─────────────────────────────────────────────────────────
// Formal date spelling — "ON SUNDAY, THE THIRTEENTH OF MARCH" / "TWO THOUSAND AND TWENTY-SIX"
const _ONES = ['','ONE','TWO','THREE','FOUR','FIVE','SIX','SEVEN','EIGHT','NINE','TEN','ELEVEN','TWELVE','THIRTEEN','FOURTEEN','FIFTEEN','SIXTEEN','SEVENTEEN','EIGHTEEN','NINETEEN']
const _TENS = ['','','TWENTY','THIRTY']
function ordinalWord(n) {
  const ORD = { 1:'FIRST',2:'SECOND',3:'THIRD',5:'FIFTH',8:'EIGHTH',9:'NINTH',12:'TWELFTH' }
  if (ORD[n]) return ORD[n]
  if (n < 20) return _ONES[n] + 'TH'
  const t = Math.floor(n/10), o = n % 10
  if (o === 0) return _TENS[t].replace(/Y$/, 'IETH')
  return _TENS[t] + '-' + (ORD[o] || _ONES[o] + 'TH')
}
function yearWords(y) {
  const rem = y - 2000
  if (rem <= 0) return String(y)
  if (rem < 20) return 'TWO THOUSAND AND ' + _ONES[rem]
  const t = Math.floor(rem/10), o = rem % 10
  return 'TWO THOUSAND AND ' + _TENS[t] + (o ? '-' + _ONES[o] : '')
}

function ImageTemplateCard({ template, wedding, event }) {
  const W = 340
  const H = Math.round(W / template.aspect)
  const p1 = titleCase(wedding?.partner1_name) || 'Partner One'
  const p2 = titleCase(wedding?.partner2_name) || 'Partner Two'
  const city = wedding?.city || ''
  const evVenue = event?.venue || ''
  const d = event?.date ? new Date(event.date) : null
  const z = template.zone
  const { ink, accent, sub } = template
  const px = f => `${Math.round(W * f)}px`
  const cap = { fontFamily:'Cormorant Garamond, serif', textTransform:'uppercase', fontWeight:600 }
  // Split a name into a big first word + a smaller remainder (surname/middle).
  const split = (n) => { const parts = (n || '').trim().split(/\s+/); return { first: parts[0] || (n || ''), rest: parts.slice(1).join(' ') } }
  const s1 = split(p1), s2 = split(p2)

  const wrap = (content) => (
    <div style={{ position:'relative', width:W, height:H, borderRadius:10, overflow:'hidden', boxShadow:'0 14px 44px rgba(0,0,0,0.18)' }}>
      <img src={template.src} alt="" style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover', display:'block' }}/>
      <div style={{ position:'absolute', left:`${z.left*100}%`, right:`${z.right*100}%`, top:`${z.top*100}%`, bottom:`${z.bottom*100}%`, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center' }}>
        {content}
      </div>
    </div>
  )

  // ── Editorial layout (Playfair italic names, script ampersand, clean date) ──
  if (template.layout === 'editorial') {
    return wrap(<>
      <p style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.032), letterSpacing:'0.3em', color:sub, margin:0 }}>The Wedding Of</p>
      <h2 style={{ fontFamily:'Great Vibes, cursive', fontWeight:400, textTransform:'capitalize', fontSize:px(0.15), color:ink, lineHeight:1.0, margin:`${px(0.03)} 0 0` }}>{p1}</h2>
      <p style={{ fontFamily:'Great Vibes, cursive', fontSize:px(0.1), color:accent, lineHeight:1, margin:`${px(0.008)} 0` }}>&amp;</p>
      <h2 style={{ fontFamily:'Great Vibes, cursive', fontWeight:400, textTransform:'capitalize', fontSize:px(0.15), color:ink, lineHeight:1.0, margin:0 }}>{p2}</h2>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:px(0.022), margin:`${px(0.05)} 0 ${px(0.04)}`, width:'72%' }}>
        <div style={{ flex:1, height:1, background:accent, opacity:0.45 }}/>
        <div style={{ width:px(0.014), height:px(0.014), borderRadius:'50%', background:accent }}/>
        <div style={{ flex:1, height:1, background:accent, opacity:0.45 }}/>
      </div>
      <p style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.031), letterSpacing:'0.14em', color:ink, margin:0 }}>
        {d ? d.toLocaleDateString('en-GB',{weekday:'long', day:'numeric', month:'long', year:'numeric'}) : 'Date to be confirmed'}
      </p>
      {(evVenue || city) && <p style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.028), letterSpacing:'0.12em', color:sub, margin:`${px(0.025)} 0 0`, lineHeight:1.7 }}>{[evVenue, city].filter(Boolean).join(' · ')}</p>}
    </>)
  }

  // ── Gold-bar layout (gold caps, Alex Brush names, framed date bar) ──
  if (template.layout === 'goldbar') {
    return wrap(<>
      <p style={{ fontFamily:'DM Sans, sans-serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.027), letterSpacing:'0.2em', color:accent, margin:0, lineHeight:1.7 }}>Please join us for<br/>the wedding of</p>
      <h2 style={{ fontFamily:'Great Vibes, cursive', fontWeight:400, textTransform:'capitalize', fontSize:px(0.152), color:ink, lineHeight:1.05, margin:`${px(0.03)} 0 0` }}>{p1}</h2>
      <p style={{ fontFamily:'DM Sans, sans-serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.024), letterSpacing:'0.18em', color:accent, margin:`${px(0.01)} 0` }}>and</p>
      <h2 style={{ fontFamily:'Great Vibes, cursive', fontWeight:400, textTransform:'capitalize', fontSize:px(0.152), color:ink, lineHeight:1.05, margin:0 }}>{p2}</h2>
      {evVenue && <p style={{ fontFamily:'DM Sans, sans-serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.024), letterSpacing:'0.14em', color:accent, margin:`${px(0.05)} 0 0`, lineHeight:1.7 }}>{evVenue}</p>}
      {city && <p style={{ fontFamily:'DM Sans, sans-serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.0224), letterSpacing:'0.14em', color:accent, margin:`${px(0.004)} 0 0`, lineHeight:1.7 }}>{city}</p>}
      {d && (
        <div style={{ borderTop:`1px solid ${accent}`, borderBottom:`1px solid ${accent}`, padding:`${px(0.018)} 0`, margin:`${px(0.035)} 0 0`, display:'flex', alignItems:'center', justifyContent:'center', gap:px(0.03) }}>
          <span style={{ fontFamily:'DM Sans, sans-serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.0208), letterSpacing:'0.1em', color:ink }}>{d.toLocaleDateString('en-GB',{weekday:'long'})}</span>
          <span style={{ width:1, height:px(0.055), background:accent }}/>
          <span style={{ fontFamily:'DM Sans, sans-serif', fontWeight:700, fontSize:px(0.048), color:ink, lineHeight:1 }}>{d.getDate()}</span>
          <span style={{ width:1, height:px(0.055), background:accent }}/>
          <span style={{ fontFamily:'DM Sans, sans-serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.0208), letterSpacing:'0.08em', color:ink }}>{d.toLocaleDateString('en-GB',{month:'long'})} {d.getFullYear()}</span>
        </div>
      )}
    </>)
  }

  // ── Wreath layout (names inside the ring, date bar in the lower area) ──
  if (template.layout === 'wreath') {
    const nz = template.nameZone, bz = template.bodyZone, hz = template.headerZone
    return (
      <div style={{ position:'relative', width:W, height:H, borderRadius:10, overflow:'hidden', boxShadow:'0 14px 44px rgba(0,0,0,0.18)' }}>
        <img src={template.src} alt="" style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover', display:'block' }}/>
        {/* Header */}
        {hz && (
          <div style={{ position:'absolute', left:`${hz.left*100}%`, right:`${hz.right*100}%`, top:`${hz.top*100}%`, bottom:`${hz.bottom*100}%`, display:'flex', alignItems:'center', justifyContent:'center', textAlign:'center' }}>
            <p style={{ fontFamily:'DM Sans, sans-serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.045), letterSpacing:'0.22em', color:ink, margin:0 }}>Save the Date</p>
          </div>
        )}
        {/* Names inside the wreath */}
        <div style={{ position:'absolute', left:`${nz.left*100}%`, right:`${nz.right*100}%`, top:`${nz.top*100}%`, bottom:`${nz.bottom*100}%`, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center' }}>
          <h2 style={{ fontFamily:'Pinyon Script, cursive', fontSize:px(0.13), color:ink, lineHeight:0.95, margin:0 }}>{p1}</h2>
          <p style={{ fontFamily:'Pinyon Script, cursive', fontSize:px(0.1), color:accent, lineHeight:1, margin:`${px(0.01)} 0` }}>&amp;</p>
          <h2 style={{ fontFamily:'Pinyon Script, cursive', fontSize:px(0.13), color:ink, lineHeight:0.95, margin:0 }}>{p2}</h2>
        </div>
        {/* Date bar + venue in the lower clear area */}
        <div style={{ position:'absolute', left:`${bz.left*100}%`, right:`${bz.right*100}%`, top:`${bz.top*100}%`, bottom:`${bz.bottom*100}%`, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-start', textAlign:'center' }}>
          {d && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:px(0.028) }}>
              <span style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.034), letterSpacing:'0.08em', color:ink }}>{d.toLocaleDateString('en-GB',{weekday:'long'})}</span>
              <span style={{ width:2, height:px(0.075), background:accent }}/>
              <span style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:700, fontSize:px(0.095), color:ink, lineHeight:1 }}>{d.getDate()}</span>
              <span style={{ width:2, height:px(0.075), background:accent }}/>
              <div style={{ textAlign:'left' }}>
                <p style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:700, textTransform:'uppercase', fontSize:px(0.034), letterSpacing:'0.05em', color:ink, margin:0, lineHeight:1.05 }}>{d.toLocaleDateString('en-GB',{month:'long'})}</p>
                <p style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, fontSize:px(0.03), color:ink, margin:0, lineHeight:1.1 }}>{d.getFullYear()}</p>
              </div>
            </div>
          )}
          {(evVenue || city) && <p style={{ fontFamily:'DM Sans, sans-serif', fontWeight:500, textTransform:'uppercase', fontSize:px(0.026), letterSpacing:'0.1em', color:sub, margin:`${px(0.03)} 0 0`, lineHeight:1.7 }}>{[evVenue, city].filter(Boolean).join(' · ')}</p>}
        </div>
      </div>
    )
  }

  // ── Pressed layout (serif all-caps names, script "and", bold date line) ──
  if (template.layout === 'pressed') {
    return wrap(<>
      <p style={{ ...cap, fontSize:px(0.03), letterSpacing:'0.18em', color:sub, margin:0, lineHeight:1.75 }}>You are invited to the<br/>wedding of</p>
      <h2 style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.125), letterSpacing:'0.04em', color:ink, lineHeight:1.0, margin:`${px(0.03)} 0 0` }}>{p1}</h2>
      <p style={{ fontFamily:'Great Vibes, cursive', fontSize:px(0.072), color:accent, lineHeight:1, margin:`${px(0.004)} 0` }}>and</p>
      <h2 style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.125), letterSpacing:'0.04em', color:ink, lineHeight:1.0, margin:0 }}>{p2}</h2>
      <p style={{ ...cap, fontWeight:700, fontSize:px(0.05), letterSpacing:'0.12em', color:ink, margin:`${px(0.045)} 0 0` }}>
        {d ? d.toLocaleDateString('en-GB',{day:'numeric', month:'long', year:'numeric'}).toUpperCase() : 'DATE TO BE CONFIRMED'}
      </p>
      {d && <p style={{ ...cap, fontSize:px(0.028), letterSpacing:'0.12em', color:sub, margin:`${px(0.018)} 0 0` }}>{d.toLocaleDateString('en-GB',{weekday:'long'}).toUpperCase()}</p>}
      {(evVenue || city) && <p style={{ ...cap, fontSize:px(0.026), letterSpacing:'0.1em', color:sub, margin:`${px(0.012)} 0 0`, lineHeight:1.7 }}>{[evVenue, city].filter(Boolean).join(', ')}</p>}
    </>)
  }

  // ── Burgundy layout (left-aligned, serif names, script "And") — inv6 ──
  if (template.layout === 'burgundy') {
    return (
      <div style={{ position:'relative', width:W, height:H, borderRadius:10, overflow:'hidden', boxShadow:'0 14px 44px rgba(0,0,0,0.18)' }}>
        <img src={template.src} alt="" style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', display:'block' }}/>
        <div style={{ position:'absolute', left:`${z.left*100}%`, right:`${z.right*100}%`, top:`${z.top*100}%`, bottom:`${z.bottom*100}%`, display:'flex', flexDirection:'column', alignItems:'flex-end', justifyContent:'center', textAlign:'right' }}>
          <p style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.032), letterSpacing:'0.2em', color:sub, margin:0 }}>The Wedding Of</p>
          <h2 style={{ fontFamily:'Playfair Display, serif', fontWeight:700, fontSize:px(0.12), color:ink, lineHeight:1.05, margin:`${px(0.03)} 0 0` }}>{p1}</h2>
          <p style={{ fontFamily:'Great Vibes, cursive', fontSize:px(0.078), color:accent, lineHeight:1, margin:`${px(0.004)} 0` }}>And</p>
          <h2 style={{ fontFamily:'Playfair Display, serif', fontWeight:700, fontSize:px(0.12), color:ink, lineHeight:1.05, margin:0 }}>{p2}</h2>
          <p style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:700, textTransform:'uppercase', fontSize:px(0.033), letterSpacing:'0.08em', color:ink, margin:`${px(0.06)} 0 0`, lineHeight:1.4 }}>
            {d ? d.toLocaleDateString('en-GB',{day:'numeric', month:'long', year:'numeric'}).toUpperCase() : 'DATE TO BE CONFIRMED'}
          </p>
          {d && <p style={{ fontFamily:'Cormorant Garamond, serif', fontStyle:'italic', fontSize:px(0.034), color:sub, margin:`${px(0.01)} 0 0` }}>{d.toLocaleDateString('en-GB',{weekday:'long'}).toLowerCase()}</p>}
          {evVenue && <p style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:700, textTransform:'uppercase', fontSize:px(0.032), letterSpacing:'0.06em', color:ink, margin:`${px(0.05)} 0 0`, lineHeight:1.4 }}>{evVenue}</p>}
          {city && <p style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.027), letterSpacing:'0.05em', color:sub, margin:`${px(0.008)} 0 0`, lineHeight:1.4 }}>{city}</p>}
        </div>
      </div>
    )
  }

  // ── Leaf layout (centered, surnames, "&" with side rules) — inv7 ──
  if (template.layout === 'leaf') {
    return wrap(<>
      <p style={{ fontFamily:'Cormorant Garamond, serif', fontStyle:'italic', fontSize:px(0.034), color:sub, margin:0, lineHeight:1.7 }}>Together with their families,<br/>you're invited to celebrate the love of</p>
      <h2 style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.105), letterSpacing:'0.06em', color:ink, lineHeight:1.0, margin:`${px(0.05)} 0 0` }}>{s1.first}</h2>
      {s1.rest && <p style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.038), letterSpacing:'0.2em', color:sub, margin:`${px(0.008)} 0 0` }}>{s1.rest}</p>}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:px(0.025), margin:`${px(0.028)} 0`, width:'62%' }}>
        <div style={{ flex:1, height:1, background:accent, opacity:0.6 }}/>
        <span style={{ fontFamily:'Cormorant Garamond, serif', fontSize:px(0.06), color:accent, lineHeight:1 }}>&amp;</span>
        <div style={{ flex:1, height:1, background:accent, opacity:0.6 }}/>
      </div>
      <h2 style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.105), letterSpacing:'0.06em', color:ink, lineHeight:1.0, margin:0 }}>{s2.first}</h2>
      {s2.rest && <p style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.038), letterSpacing:'0.2em', color:sub, margin:`${px(0.008)} 0 0` }}>{s2.rest}</p>}
      <p style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, fontSize:px(0.036), color:ink, margin:`${px(0.06)} 0 0`, lineHeight:1.6 }}>
        {d ? d.toLocaleDateString('en-GB',{weekday:'long', day:'numeric', month:'long', year:'numeric'}) : 'Date to be confirmed'}
      </p>
      {evVenue && <p style={{ fontFamily:'Cormorant Garamond, serif', fontSize:px(0.034), color:sub, margin:`${px(0.012)} 0 0` }}>{evVenue}</p>}
      {city && <p style={{ fontFamily:'Cormorant Garamond, serif', fontSize:px(0.034), color:sub, margin:`${px(0.004)} 0 0` }}>{city}</p>}
    </>)
  }

  // ── Gold-leaf layout (script connectors, surnames) — inv8 ──
  if (template.layout === 'goldleaf') {
    return wrap(<>
      <p style={{ ...cap, fontSize:px(0.03), letterSpacing:'0.22em', color:sub, margin:0 }}>Kindly join us for</p>
      <p style={{ fontFamily:'Great Vibes, cursive', fontSize:px(0.082), color:ink, lineHeight:1, margin:`${px(0.012)} 0 ${px(0.022)}` }}>the Wedding of</p>
      <h2 style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.108), letterSpacing:'0.05em', color:ink, lineHeight:1, margin:0 }}>{s1.first}</h2>
      {s1.rest && <p style={{ ...cap, fontSize:px(0.034), letterSpacing:'0.18em', color:sub, margin:`${px(0.008)} 0 0` }}>{s1.rest}</p>}
      <p style={{ fontFamily:'Great Vibes, cursive', fontSize:px(0.06), color:accent, lineHeight:1, margin:`${px(0.015)} 0` }}>and</p>
      <h2 style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, textTransform:'uppercase', fontSize:px(0.108), letterSpacing:'0.05em', color:ink, lineHeight:1, margin:0 }}>{s2.first}</h2>
      {s2.rest && <p style={{ ...cap, fontSize:px(0.034), letterSpacing:'0.18em', color:sub, margin:`${px(0.008)} 0 0` }}>{s2.rest}</p>}
      <p style={{ ...cap, fontWeight:700, fontSize:px(0.034), letterSpacing:'0.1em', color:ink, margin:`${px(0.05)} 0 0` }}>
        {d ? d.toLocaleDateString('en-GB',{weekday:'long', day:'numeric', month:'long', year:'numeric'}).toUpperCase() : 'DATE TO BE CONFIRMED'}
      </p>
      {evVenue && <p style={{ ...cap, fontWeight:700, fontSize:px(0.032), letterSpacing:'0.08em', color:ink, margin:`${px(0.03)} 0 0` }}>{evVenue}</p>}
      {city && <p style={{ ...cap, fontSize:px(0.028), letterSpacing:'0.06em', color:sub, margin:`${px(0.008)} 0 0` }}>{city}</p>}
    </>)
  }

  // ── Classic layout (date pill, original Peony Garden look) ──
  return wrap(<>
    <p style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, fontSize:px(0.031), letterSpacing:'0.16em', color:sub, textTransform:'uppercase', margin:0 }}>Together with our families</p>
    <h2 style={{ fontFamily:'Great Vibes, cursive', fontWeight:400, textTransform:'capitalize', fontSize:px(0.135), color:ink, lineHeight:1.05, margin:`${px(0.02)} 0 0` }}>{p1}</h2>
    <p style={{ fontFamily:'Cormorant Garamond, serif', fontStyle:'italic', fontSize:px(0.05), color:accent, margin:`${px(0.004)} 0` }}>and</p>
    <h2 style={{ fontFamily:'Great Vibes, cursive', fontWeight:400, textTransform:'capitalize', fontSize:px(0.135), color:ink, lineHeight:1.05, margin:0 }}>{p2}</h2>
    <p style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, fontSize:px(0.029), letterSpacing:'0.09em', color:sub, textTransform:'uppercase', margin:`${px(0.035)} 0 0`, lineHeight:1.55 }}>Request the pleasure of your company at their wedding</p>
    {d ? (
      <div style={{ display:'flex', alignItems:'center', gap:px(0.025), margin:`${px(0.03)} 0` }}>
        <span style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, fontSize:px(0.03), letterSpacing:'0.06em', color:ink, textTransform:'uppercase' }}>{d.toLocaleDateString('en-GB',{month:'short'}).toUpperCase()}</span>
        <span style={{ width:1, height:px(0.05), background:accent, opacity:0.5 }}/>
        <span style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:700, fontSize:px(0.075), color:ink, lineHeight:1 }}>{d.getDate()}</span>
        <span style={{ width:1, height:px(0.05), background:accent, opacity:0.5 }}/>
        <span style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, fontSize:px(0.03), color:ink }}>{d.getFullYear()}</span>
      </div>
    ) : <p style={{ fontFamily:'Cormorant Garamond, serif', fontSize:px(0.034), color:ink, margin:`${px(0.03)} 0` }}>Date to be confirmed</p>}
    {evVenue && <p style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:600, fontSize:px(0.035), color:ink, margin:0 }}>{evVenue}</p>}
    {city && <p style={{ fontFamily:'Cormorant Garamond, serif', fontSize:px(0.03), color:sub, margin:`${px(0.005)} 0 0` }}>{city}</p>}
  </>)
}

// ─────────────────────────────────────────────────────────
// CARD ROUTER
// ─────────────────────────────────────────────────────────
function CardRenderer({ template, wedding, event }) {
  if (template.type === 'image') return <ImageTemplateCard template={template} wedding={wedding} event={event} />

  // Custom uploaded image
  if (template.id === 'custom_upload' && template.customImage) {
    return (
      <div style={{ width:340, borderRadius:16, overflow:'hidden', boxShadow:'0 8px 32px rgba(0,0,0,0.15)' }}>
        <img src={template.customImage} alt="Custom invitation" style={{ width:'100%', display:'block' }}/>
      </div>
    )
  }


  if (template.tier === 'free') return <FreeCard id={template.id} wedding={wedding} event={event} />
  if (template.id === 'floral_blush') return <BlushFloralCard wedding={wedding} event={event} />
  if (template.id === 'gold_wreath')  return <GoldWreathCard  wedding={wedding} event={event} />
  if (template.id === 'navy_gold')    return <NavyGoldCard    wedding={wedding} event={event} />
  if (template.id === 'bold_mono')    return <BoldMonoCard    wedding={wedding} event={event} />
  if (template.id === 'ivory_emboss') return <IvoryEmbossCard wedding={wedding} event={event} />
  return null
}

// ─────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────
export default function InvitationDesigner({ event }) {
  const { wedding , isPremium } = useWedding()
  const navigate = useNavigate()
  const [uploadedInvite, setUploadedInvite] = useState(null)
  const [selected, setSelected] = useState(FREE_TEMPLATES[0])
  const [copied, setCopied] = useState(false)

  const shareLink = `${window.location.origin}/w/${wedding?.slug}/invite/${event?.id}`

  function copyLink() {
    navigator.clipboard.writeText(shareLink)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  async function downloadInvite() {
    const el = document.getElementById('invite-preview')
    if (!el) { alert('Please select a template first.'); return }
    try {
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: el.offsetWidth,
        height: el.offsetHeight,
      })
      const link = document.createElement('a')
      link.download = `invitation-${(event?.type||'wedding').replace(/[^a-z0-9]/gi,'-').toLowerCase()}.png`
      link.href = canvas.toDataURL('image/png', 0.95)
      document.body.appendChild(link); link.click(); document.body.removeChild(link)
    } catch(e) {
      console.error('html2canvas error:', e)
      alert('Download failed: ' + e.message)
    }
  }

  async function shareViaWhatsApp() {
    const couple = `${wedding?.partner1_name||''} & ${wedding?.partner2_name||''}`
    const link = `${window.location.origin}/w/${wedding?.slug||''}`
    const el = document.getElementById('invite-preview')
    // Try to download image first, then open WhatsApp
    if (el) {
      try {
        const canvas = await html2canvas(el, { scale:2, useCORS:true, allowTaint:true, backgroundColor:'#ffffff' })
        // Download image
        const a = document.createElement('a')
        a.download = 'invitation.png'
        a.href = canvas.toDataURL('image/png')
        document.body.appendChild(a); a.click(); document.body.removeChild(a)
        // Brief delay then open WhatsApp
        setTimeout(() => {
          window.open(`https://wa.me/?text=${encodeURIComponent("You're invited to the wedding of " + couple + "!\nAttach the downloaded image and share!\nRSVP & details: " + link)}`, '_blank')
        }, 800)
        return
      } catch(e) { /* fallback to text only */ }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent("You're invited to the wedding of " + couple + "! RSVP & details: " + link)}`, '_blank')
  }




  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.5rem' }}>
      <div>
        <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.2rem', margin:'0 0 0.2rem' }}>Invitations</h2>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#6B6B6B', margin:0 }}>Pick a style — your names and event details are applied automatically.</p>
      </div>

      <div style={{ display:'flex', gap:'1.5rem', flexWrap:'wrap', alignItems:'flex-start', width:'100%' }}>

        {/* ── Template Picker ── */}
        <div style={{ flex:'1 1 200px', maxWidth:220 }}>

          <p className="label" style={{ marginBottom:'0.5rem' }}>Free Styles</p>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem', marginBottom:'1.25rem' }}>
            {FREE_TEMPLATES.map(t => (
              <button key={t.id} onClick={() => setSelected(t)} style={{
                display:'flex', alignItems:'center', gap:'0.625rem', padding:'0.5rem 0.75rem',
                border:`2px solid ${selected.id===t.id?'#C4826A':'#E5E0D8'}`,
                background: selected.id===t.id?'rgba(196,130,106,0.06)':'white',
                borderRadius:'0.75rem', cursor:'pointer', transition:'all 0.15s', width:'100%'
              }}>
                <div style={{ display:'flex', gap:'2px', flexShrink:0 }}>
                  {t.swatch.map((col,i)=><div key={i} style={{ width:10, height:22, borderRadius:3, background:col, border:'1px solid rgba(0,0,0,0.08)' }}/>)}
                </div>
                <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', fontWeight:selected.id===t.id?700:500, color:'#1A1A1A', flex:1 }}>{t.name}</span>
                {selected.id===t.id && <Check size={13} color="#C4826A"/>}
              </button>
            ))}
          </div>

          {/* Premium section header — gold gradient title */}
          <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', marginBottom:'0.5rem' }}>
            <Crown size={12} color="#C9A84C"/>
            <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:800, letterSpacing:'0.07em', textTransform:'uppercase', background:'linear-gradient(135deg, #B8860B, #D4AF37, #C9A84C)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
              Premium Styles
            </span>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem' }}>
            {PREMIUM_TEMPLATES.map(t => {
              const locked = !isPremium
              return (
                <button key={t.id} onClick={() => setSelected(t)} style={{
                  display:'flex', alignItems:'center', gap:'0.625rem', padding:'0.5rem 0.75rem',
                  border:`2px solid ${selected.id===t.id?t.titleColor:'#E5E0D8'}`,
                  background: selected.id===t.id?`${t.titleColor}10`:locked?'#FAFAF8':'white',
                  borderRadius:'0.75rem', cursor:'pointer', transition:'all 0.15s', width:'100%', opacity:locked?0.8:1
                }}>
                  <div style={{ display:'flex', gap:'2px', flexShrink:0 }}>
                    {t.swatch.map((col,i)=><div key={i} style={{ width:10, height:22, borderRadius:3, background:col, border:'1px solid rgba(0,0,0,0.08)' }}/>)}
                  </div>
                  <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', fontWeight:selected.id===t.id?700:500, color:selected.id===t.id?t.titleColor:'#1A1A1A', flex:1 }}>{t.name}</span>
                  {locked ? <Lock size={12} color="#BCBCBC"/> : selected.id===t.id ? <Check size={13} color={t.titleColor}/> : null}
                </button>
              )
            })}
          </div>


          {/* Upload own invite */}
          {isPremium && (
            <div style={{ marginTop:'0.875rem', paddingTop:'0.875rem', borderTop:'1px solid #E5E0D8' }}>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#6B6B6B', textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 0.5rem' }}>Upload Your Own</p>
              <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.5rem 0.75rem', border:'1.5px dashed #E5E0D8', borderRadius:'0.75rem', cursor:'pointer', background:'#FFFFFF', transition:'all 0.15s' }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor='#C4826A'}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor='#E5E0D8'}}>
                <Upload size={14} color="#C4826A"/>
                <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#C4826A', fontWeight:600 }}>PNG or JPG (max 10MB)</span>
                <input type="file" accept="image/png,image/jpeg" style={{ display:'none' }}
                  onChange={e=>{
                    const file = e.target.files[0]
                    if (!file) return
                    if (file.size > 10*1024*1024) { alert('Image must be under 10MB'); return }
                    const reader = new FileReader()
                    reader.onload = ev => {
                      setSelected({ id:'custom_upload', name:'My Design', tier:'premium', customImage:ev.target.result })
                    }
                    reader.readAsDataURL(file)
                  }}/>
              </label>
            </div>
          )}
          {!isPremium && (
            <button className="btn-primary" onClick={() => navigate('/pricing')}
              style={{ width:'100%', justifyContent:'center', marginTop:'0.875rem', fontSize:'0.78rem', padding:'0.6rem', display:'flex', alignItems:'center', gap:'0.4rem', background:'linear-gradient(135deg, #B8860B, #D4AF37)' }}>
              <Crown size={13}/> Unlock All Styles
            </button>
          )}
        </div>

        {/* ── Preview + Share ── */}
        <div style={{ flex:'999 1 280px', minWidth:0, display:'flex', flexDirection:'column', alignItems:'center', gap:'1.25rem' }}>
          {/* Card preview — larger scale */}
          <div style={{ width:'100%', display:'flex', justifyContent:'center', overflow:'hidden' }}>
            <div id="invite-preview">
              <CardRenderer template={selected} wedding={wedding} event={event}/>
            </div>
          </div>

          {/* Share panel */}
          <div className="card" style={{ width:'100%', maxWidth:360, padding:'1.25rem' }}>
            {selected.tier === 'premium' && !isPremium ? (
              // ── Upgrade prompt ──
              <div style={{ textAlign:'center' }}>
                <div style={{ width:44, height:44, background:'linear-gradient(135deg, rgba(184,134,11,0.12), rgba(212,175,55,0.15))', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 0.875rem' }}>
                  <Crown size={20} color="#C9A84C" />
                </div>
                <p style={{ fontFamily:'Cormorant Garamond, serif', fontWeight:400, fontSize:'1rem', color:'#1A1A1A', margin:'0 0 0.4rem' }}>
                  Unlock <em>{selected.name}</em>
                </p>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#6B6B6B', margin:'0 0 1.25rem', lineHeight:1.6 }}>
                  Upgrade to Premium to send this design to your guests.
                </p>
                <button className="btn-primary" onClick={() => navigate('/pricing')}
                  style={{ width:'100%', justifyContent:'center', display:'flex', alignItems:'center', gap:'0.5rem', background:'linear-gradient(135deg, #B8860B, #D4AF37)' }}>
                  <Crown size={14} /> Upgrade to Send
                </button>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#BCBCBC', margin:'0.75rem 0 0' }}>
                  Free templates are always available to send
                </p>
              </div>
            ) : (
              // ── Normal share panel ──
              <>
                <p className="label" style={{ marginBottom:'0.75rem' }}>Share this invitation</p>

                <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem', marginTop:'0.5rem' }}>
                  <button onClick={downloadInvite} className="btn-secondary" style={{ width:'100%', justifyContent:'center', padding:'0.75rem', fontSize:'0.85rem', display:'flex', alignItems:'center', gap:'0.4rem' }}>
                    <Download size={15}/> Download Image
                  </button>
                  <button onClick={shareViaWhatsApp} style={{ width:'100%', justifyContent:'center', padding:'0.75rem', fontSize:'0.85rem', display:'flex', alignItems:'center', gap:'0.4rem', background:'#25D366', color:'white', border:'none', borderRadius:100, cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontWeight:700 }}>
                    <MessageCircle size={15}/> Share via WhatsApp
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
